import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import type { ChannelReadiness as ReadinessData } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'

interface ChannelReadinessProps {
  data: ReadinessData
}

export function ChannelReadiness({ data }: ChannelReadinessProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <OverallBadge status={data.overall_status} label={t('amazonListing.report.channelOverallStatus')} />

      {data.text_rules.length > 0 && (
        <SectionCard title={t('amazonListing.report.channelTextRules')}>
          <RulesTable
            headers={[
              t('amazonListing.report.channelField'),
              t('amazonListing.report.channelRule'),
              '',
              t('amazonListing.report.channelExpected'),
              t('amazonListing.report.channelActual'),
            ]}
            rows={data.text_rules.map((r) => ({
              cols: [r.field, r.rule, r.expected, r.actual],
              status: r.status,
            }))}
          />
        </SectionCard>
      )}

      {data.image_rules.length > 0 && (
        <SectionCard title={t('amazonListing.report.channelImageRules')}>
          <RulesTable
            headers={[
              t('amazonListing.report.channelRule'),
              '',
              t('amazonListing.report.channelExpected'),
              t('amazonListing.report.channelActual'),
            ]}
            rows={data.image_rules.map((r) => ({
              cols: [r.rule, r.expected, r.actual],
              status: r.status,
            }))}
          />
        </SectionCard>
      )}
    </div>
  )
}

function OverallBadge({ status, label }: { status: string; label: string }) {
  const cfg = statusConfig(status)
  return (
    <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border ${cfg.border} ${cfg.bg}`}>
      <StatusIcon status={status} size="w-6 h-6" />
      <span className={`text-base font-semibold ${cfg.text}`}>{label}: {status.toUpperCase()}</span>
    </div>
  )
}

function StatusIcon({ status, size = 'w-4 h-4' }: { status: string; size?: string }) {
  if (status === 'pass') return <CheckCircle2 className={`${size} text-green-600`} />
  if (status === 'fail') return <XCircle className={`${size} text-red-600`} />
  return <AlertTriangle className={`${size} text-yellow-600`} />
}

function statusConfig(status: string) {
  if (status === 'pass') return { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300' }
  if (status === 'fail') return { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' }
  return { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300' }
}

interface RulesTableProps {
  headers: string[]
  rows: Array<{ cols: string[]; status: string }>
}

function RulesTable({ headers, rows }: RulesTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-neutral-100 dark:border-neutral-700/50">
              {row.cols.map((col, j) => j === 0 ? (
                <td key={j} className="py-2 px-3 text-neutral-800 dark:text-neutral-200">{col}</td>
              ) : (
                <td key={j} className="py-2 px-3 text-neutral-600 dark:text-neutral-400">{col}</td>
              ))}
              <td className="py-2 px-3"><StatusIcon status={row.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
